import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon, SparklesIcon } from 'lucide-react';
interface ExecutiveSummaryProps {
  open: boolean;
  onClose: () => void;
}
export function ExecutiveSummary({
  open,
  onClose
}: ExecutiveSummaryProps) {
  return <AnimatePresence data-id="element-31" data-mp="217k7hlu9cinz">
      {open && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 md:p-8" onClick={onClose} data-id="element-32" data-mp="1wbwk9bzc5zl4">
          <motion.div initial={{
        scale: 0.96,
        opacity: 0,
        y: 20
      }} animate={{
        scale: 1,
        opacity: 1,
        y: 0
      }} exit={{
        scale: 0.96,
        opacity: 0,
        y: 20
      }} transition={{
        type: 'spring',
        damping: 25,
        stiffness: 300
      }} className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[92vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()} data-id="element-33" data-mp="1id5ag1rib02s">
            {/* Header */}
            <div className="flex items-center justify-between px-6 md:px-10 py-5 border-b border-slate-200 bg-white sticky top-0 z-10" data-id="element-34" data-mp="2eicklk7cec24">
              <div className="flex items-center gap-3" data-id="element-35" data-mp="1lu8dqvd1bsxu">
                <div className="w-10 h-10 rounded-xl bg-[#6C2BD9] flex items-center justify-center" data-id="element-36" data-mp="1lv07e0n2nglh">
                  <SparklesIcon className="w-5 h-5 text-white" data-id="element-37" data-mp="1qs9vncyq5m7q" />
                </div>
                <div data-id="element-38" data-mp="1uvy7i7ue33ys">
                  <div className="text-[11px] uppercase tracking-[0.18em] text-[#6C2BD9] font-semibold" data-id="element-39" data-mp="1ut25a7bidt4j">
                    Snakker · Investor Pitch
                  </div>
                  <h2 className="text-lg md:text-xl font-bold text-slate-900 leading-tight" data-id="element-40" data-mp="3690j0ohmt3c3">
                    Executive Summary
                  </h2>
                </div>
              </div>
              <button onClick={onClose} className="w-10 h-10 rounded-full hover:bg-slate-100 flex items-center justify-center transition-colors" aria-label="Close" data-id="element-41" data-mp="125p7ueh7yeoj">
                <XIcon className="w-5 h-5 text-slate-600" data-id="element-42" data-mp="1b52j9cowldbw" />
              </button>
            </div>

            <article className="overflow-y-auto px-6 md:px-12 py-10 prose prose-slate max-w-none" data-id="element-43" data-mp="nfi0atsr2g2m">
              <p className="text-xl md:text-2xl font-semibold text-slate-900 leading-snug mb-8" data-id="element-44" data-mp="jdwgsplpn3jg">
                Snakker is the AI-native engine for{' '}
                <span className="text-[#6C2BD9]" data-id="element-45" data-mp="sfmico9b3iuy">
                  Legacy Brand Optimization
                </span>{' '}
                — unlocking value from pharma's revenue tail through continuous,
                scalable optimization of mature and off-patent brands.
              </p>

              <h3 className="text-base font-bold text-[#6C2BD9] uppercase tracking-wider mt-8 mb-2" data-id="element-46" data-mp="2bf3kxsbctgj0">
                The Pharma Lifecycle
              </h3>
              <p className="text-slate-700 leading-relaxed" data-id="element-47" data-mp="2g7wr8kmh0wx7">
                Every pharma product moves through four phases.{' '}
                <strong data-id="element-48" data-mp="27d8l6s92nb7c">R&D</strong> runs deep into the negative through
                discovery and clinical development. <strong data-id="element-49" data-mp="2v32x97lkgvqj">Launch</strong> is
                the rapid rise as the brand reaches market and starts generating
                revenue. <strong data-id="element-50" data-mp="2z4efut60wgp8">Mature</strong> is the peak — strong share,
                engaged prescribers, and the cash that funds the rest of the
                portfolio. Then comes <strong data-id="element-51" data-mp="2q3gll2ldznju">Legacy</strong>: patents expire,
                attention shifts to newer assets, marketing investment is
                pulled, and revenue typically falls off a cliff and flattens
                into a thin tail just above zero. Snakker focuses entirely on
                this Legacy phase — the strategic move is to{' '}
                <em data-id="element-52" data-mp="1u0ucp8j7yua5">soften the cliff and extend the tail</em>, where small
                improvements compound into enormous portfolio value.
              </p>

              <h3 className="text-base font-bold text-[#6C2BD9] uppercase tracking-wider mt-8 mb-2" data-id="element-53" data-mp="1l7h99cnj4a9t">
                The Market
              </h3>
              <p className="text-slate-700 leading-relaxed" data-id="element-54" data-mp="1g5i6o4zm5hdx">
                Roughly <strong data-id="element-55" data-mp="2cx28llqcdsss">30–35% of big pharma revenue</strong> comes from
                mature and legacy brands, and{' '}
                <strong data-id="element-56" data-mp="28tw7stm00ttp">$180–300B+ in revenue</strong> is facing loss of
                exclusivity in the coming years. This is the part of the
                portfolio that quietly funds the next generation of innovation —
                and it is the most under-optimized segment in the industry.
              </p>

              <h3 className="text-base font-bold text-[#6C2BD9] uppercase tracking-wider mt-8 mb-2" data-id="element-57" data-mp="nixej2bbd19b">
                The Problem
              </h3>
              <p className="text-slate-700 leading-relaxed" data-id="element-58" data-mp="dg1xiar6lp96">
                When patents expire, generic competition can cause sales to drop
                more than 90% almost overnight for small molecules — and
                marketing investment is stopped immediately. Sales teams are
                reassigned, budgets cut, and when the gap between revenue and
                cost approaches zero the product is taken off the market. The
                real problem isn't that these brands lack value — it's that{' '}
                <strong data-id="element-59" data-mp="1164k4bcbu30e">traditional marketing is simply too expensive</strong>{' '}
                to justify on declining revenue, so brands die earlier than they
                need to. Biologics face the same end state, just on a slower
                curve.
              </p>

              <h3 className="text-base font-bold text-[#6C2BD9] uppercase tracking-wider mt-8 mb-2" data-id="element-60" data-mp="37wlvvkdqxwjm">
                The Business Model
              </h3>
              <p className="text-slate-700 leading-relaxed" data-id="element-61" data-mp="34smui5wmaecu">
                Snakker takes over the annual <strong data-id="element-62" data-mp="2zul8pd7t40xo">campaign calendar</strong>{' '}
                for portfolios of legacy brands across multiple countries —
                typically <strong data-id="element-63" data-mp="3esx4zir9p7ob">4–6 campaigns per product per year</strong>.
                One contract per account, one operating partner, predictable
                annual revenue. Four accounts are already running this model:{' '}
                <strong data-id="element-64" data-mp="3npnybnefq8f7">GSK</strong> (Early Stage III — 5 products in Nigeria,
                Kenya & Morocco, expanding to 7 Gulf countries),{' '}
                <strong data-id="element-65" data-mp="1bebhiq5l8gj">Solventum</strong> (Late Stage II — MedSurge + Dental
                divisions across UK, Germany, Nordics & South Africa),{' '}
                <strong data-id="element-66" data-mp="3sl3ckggcu6c5">Leo Pharma</strong> (Stage III — 5 products across UK,
                Spain, Brazil, Poland & Greece) and{' '}
                <strong data-id="element-67" data-mp="6cbtnvb5y2i1">Roche Pharmaceutical</strong> (Early Stage II — 3
                products across China, Canada, Italy, Germany & Ivory Coast).
                Each one
                represents a potential of{' '}
                <strong data-id="element-68" data-mp="txmjwfl1j10a">100–135 countries</strong> and{' '}
                <strong data-id="element-69" data-mp="fzenc8rx21pn">20–60 brands</strong> — the standard big-pharma
                footprint of 20–60 brands × 100–135 countries × 4–6
                campaigns/year.
              </p>

              <h3 className="text-base font-bold text-[#6C2BD9] uppercase tracking-wider mt-8 mb-2" data-id="element-70" data-mp="69bnppqahpic">
                The Unlocked Value
              </h3>
              <p className="text-slate-700 leading-relaxed" data-id="element-71" data-mp="b1mn8rh4b9ad">
                Even a <strong data-id="element-72" data-mp="3teu847k3lj3d">1% improvement</strong> across a legacy portfolio
                represents tens of millions of euros per year. Modeled against a
                pharma division of €5–60B with 35% legacy revenue, the
                unmanaged, under-optimized value sitting on the table is
                enormous — and it compounds every quarter it's left alone.
              </p>

              <h3 className="text-base font-bold text-[#6C2BD9] uppercase tracking-wider mt-8 mb-2" data-id="element-73" data-mp="7s3hchwewn97">
                Why AI Alone Isn't Enough
              </h3>
              <p className="text-slate-700 leading-relaxed" data-id="element-74" data-mp="2qe3kuht1vhf">
                Generative AI dramatically reduces the cost of content creation,
                making it theoretically possible to support the long tail. But
                AI alone is not enough.{' '}
                <strong data-id="element-75" data-mp="3kyvgw4349ax7">Execution is the bottleneck</strong> — what actually
                moves legacy revenue is strategy, compliance, distribution and
                measurement working together as a system.
              </p>

              <h3 className="text-base font-bold text-[#6C2BD9] uppercase tracking-wider mt-8 mb-2" data-id="element-76" data-mp="3b67odlj65t4a">
                What Makes Us Structurally Different
              </h3>
              <p className="text-slate-700 leading-relaxed" data-id="element-77" data-mp="3272oaarylrq0">
                Backed by an <strong data-id="element-78" data-mp="2x89a0g4ulwos">Innobooster grant</strong>, we have turned
                thirty years of pharma campaign models into AI workflows.
                Storyboarding and creation now happen in minutes, not months,
                producing short, engaging, TA-relevant campaigns ready for
                deployment. Four things set Snakker apart:{' '}
                <strong data-id="element-79" data-mp="1mmtpr7jjg91w">deep vertical expertise and master agreements</strong>{' '}
                in regulated environments where generalist AI fails; a{' '}
                <strong data-id="element-80" data-mp="u1a7w74fkbdy">one-to-one precision delivery model</strong> versus
                agencies' broad-stroke campaigns; a{' '}
                <strong data-id="element-81" data-mp="ywz9sh3xa4df">proprietary interactive engagement platform</strong>{' '}
                that drives measurable outcomes; and a{' '}
                <strong data-id="element-82" data-mp="17rnhedwyzy5e">compounding behavioural flywheel</strong> in which data
                refines the model weekly, widening the gap over time.
              </p>

              <h3 className="text-base font-bold text-[#6C2BD9] uppercase tracking-wider mt-8 mb-2" data-id="element-83" data-mp="b7oh7mwq7gad">
                A New Category: Campaign Service Management
              </h3>
              <p className="text-slate-700 leading-relaxed" data-id="element-84" data-mp="2b8teg5pwlji8">
                Snakker is not an agency and not a SaaS tool. We are creating a
                new category — <strong data-id="element-85" data-mp="2k9jb1bmzndx0">Campaign Service Management</strong> —{' '}
                managed pharma campaign execution powered by software economics.
              </p>

              <h3 className="text-base font-bold text-[#6C2BD9] uppercase tracking-wider mt-8 mb-2" data-id="element-86" data-mp="1sill2fc0a7f8">
                Three Levers
              </h3>
              <p className="text-slate-700 leading-relaxed" data-id="element-87" data-mp="1wl03wzfod76i">
                Our approach pulls three levers that compound on each other. We{' '}
                <strong data-id="element-88" data-mp="26a024gmcryjn">reduce erosion</strong> through continuous, data-driven
                engagement that keeps HCPs connected to the brand. We{' '}
                <strong data-id="element-89" data-mp="18tunoj5tocvo">increase profitability</strong> by replacing traditional
                marketing with AI-automated execution, widening the margin on
                every euro of legacy revenue. And we{' '}
                <strong data-id="element-90" data-mp="169er0g4s415q">prolong lifespan</strong> — lower costs and slower
                erosion keep the gap between revenue and cost positive for
                longer, so products stay on market generating value instead of
                being withdrawn prematurely.
              </p>

              <h3 className="text-base font-bold text-[#6C2BD9] uppercase tracking-wider mt-8 mb-2" data-id="element-91" data-mp="s9vihm822nsw">
                The Operating Model
              </h3>
              <p className="text-slate-700 leading-relaxed" data-id="element-92" data-mp="ik3oou7s5yph">
                Snakker runs on four pillars. We{' '}
                <strong data-id="element-93" data-mp="dmkafub31lxj">leverage existing assets</strong> — no new content
                creation required, we repurpose approved materials into
                interactive formats. We achieve{' '}
                <strong data-id="element-94" data-mp="4jsvhkj00815">rapid deployment</strong> through centralized creation
                with localized distribution. We perform{' '}
                <strong data-id="element-95" data-mp="3vt8edguec31m">behavioral data capture</strong> that moves beyond
                vanity metrics to reveal what actually drives HCP decisions. And
                we run a <strong data-id="element-96" data-mp="3q665d5rc5b9i">continuous optimization</strong> loop where
                every interaction informs the next campaign.
              </p>

              <h3 className="text-base font-bold text-[#6C2BD9] uppercase tracking-wider mt-8 mb-2" data-id="element-97" data-mp="3c7on3nk78qyz">
                What Snakker Does
              </h3>
              <p className="text-slate-700 leading-relaxed" data-id="element-98" data-mp="8pocs1r689i7">
                Snakker acts as the operating layer between strategy and
                execution. We create campaigns from approved content, turn them
                into short interactive <strong data-id="element-99" data-mp="3v23n9gi8ypoe">Snaks™</strong>, publish them
                across channels, capture behavioral data on every interaction,
                and improve campaign after campaign.
              </p>

              <h3 className="text-base font-bold text-[#6C2BD9] uppercase tracking-wider mt-8 mb-2" data-id="element-100" data-mp="rlo6pi3pln4f">
                Snaks Throughout the Customer Journey
              </h3>
              <p className="text-slate-700 leading-relaxed" data-id="element-101" data-mp="2h31wv0oqvtgq">
                The same Snak format adapts to every stage of the HCP journey:{' '}
                <strong data-id="element-102" data-mp="2pwop4gtquel6">Awareness</strong> (condition framing, diagnostic
                prompts), <strong data-id="element-103" data-mp="2pyrb8prrznkm">Education</strong> (disease state, mechanism
                of action, preference exploration), <strong data-id="element-104" data-mp="1y5qwtdvagw08">Engagement</strong>{' '}
                (live polls, peer benchmarks, speciality routing), and{' '}
                <strong data-id="element-105" data-mp="1t777d3hhl234">Conversion & Loyalty</strong> (treatment journeys,
                role-based content, congress follow-up). Every Snak captures
                behavioural data that sharpens the next campaign.
              </p>

              <h3 className="text-base font-bold text-[#6C2BD9] uppercase tracking-wider mt-8 mb-2" data-id="element-106" data-mp="225sp4wf4wd2u">
                Platform Architecture
              </h3>
              <p className="text-slate-700 leading-relaxed" data-id="element-107" data-mp="3dn0t5glvbr6h">
                Behind the product runs an AI-driven closed-loop architecture
                organized as Think · Create · Learn · Improve, built on three
                layers: the <strong data-id="element-108" data-mp="hr5xh5c8mfjk">AI Intelligence Engine</strong> (blueprint
                analysis, story generation, content creation, channel
                optimization), the <strong data-id="element-109" data-mp="rokbkyaous8p">Data Supply Chain</strong>{' '}
                (therapeutic-area themes, brand data, HCP insights, market
                intelligence), and the <strong data-id="element-110" data-mp="vpmfhsfjvsh0">Application Suite</strong> (Snak
                Composer, Snakker Player, Distribution, Analytics).
              </p>

              <h3 className="text-base font-bold text-[#6C2BD9] uppercase tracking-wider mt-8 mb-2" data-id="element-111" data-mp="15fywxbs2j5ix">
                The Moat
              </h3>
              <p className="text-slate-700 leading-relaxed" data-id="element-112" data-mp="1adqp86wmktnn">
                Defensibility comes from four compounding assets:{' '}
                <strong data-id="element-113" data-mp="1fc20mm2kjc7z">medical storytelling</strong> as codified pharma
                expertise, a{' '}
                <strong data-id="element-114" data-mp="1jddr0tt9o1h8">short-format interactive engagement platform</strong>{' '}
                driving measurable outcomes, deep{' '}
                <strong data-id="element-115" data-mp="2zvxogx43e1l9">pharma distribution</strong> relationships, and a
                privacy-safe <strong data-id="element-116" data-mp="2uwufx2owld8j">behavioural dataset</strong> that compounds
                performance with every campaign. Together they form an
                Unbreakable Flywheel that no broad player can match inside this
                regulated vertical.
              </p>

              <h3 className="text-base font-bold text-[#6C2BD9] uppercase tracking-wider mt-8 mb-2" data-id="element-117" data-mp="3nnkw9wpnwlxo">
                Short to Mid Term Strategy
              </h3>
              <p className="text-slate-700 leading-relaxed" data-id="element-118" data-mp="3dunk08b9jt7i">
                Today,{' '}
                <strong data-id="element-119" data-mp="38vu8glfjec9z">
                  85% of our sales effort goes to Stage I pilots
                </strong>
                . They close in 4–7 weeks and bring in cash, but cap at €5–10K —
                leaving the larger Stage II (€25–60K, 3–6 months), Stage III
                (€100K+, 4–8 months) and Stage IV (€1M+, 12+ months)
                opportunities under-worked. The pipeline already holds{' '}
                <strong data-id="element-120" data-mp="23p2r3jl71fhz">7 named accounts in Stage I</strong> (Nestlé HS, Bayer,
                AstraZeneca, MSD, Sanofi, Novartis, Boehringer),{' '}
                <strong data-id="element-121" data-mp="1ux0tdt2iedbe">5 in Stage II</strong> (Solventum, Menarini, Roche,
                AbbVie, CSL Vifor), and{' '}
                <strong data-id="element-122" data-mp="1fzqh4hi1l3em">GSK and Leo Pharma in Stage III</strong>. The strategy
                is to rebalance sales focus to{' '}
                <strong data-id="element-123" data-mp="1l0nywsz53vfl">10% / 20% / 30% / 40%</strong> across Stages I → IV and{' '}
                <strong data-id="element-124" data-mp="170mytfqp8s16">
                  move existing Stage I and II clients into Stage III, and Stage
                  III into Stage IV
                </strong>{' '}
                — same accounts, 10× the deal size.
              </p>

              <h3 className="text-base font-bold text-[#6C2BD9] uppercase tracking-wider mt-8 mb-2" data-id="element-125" data-mp="31bi8kldmed5j">
                Fast Marketing™ Revenue per Account
              </h3>
              <p className="text-slate-700 leading-relaxed" data-id="element-126" data-mp="3ggvzl7dozo1c">
                ACV per pharma account is built on three flat list-price units:
                a <strong data-id="element-127" data-mp="3p8fvllqy3jsm">Master Snak at €3,000</strong> (follow-up campaigns
                reusing an existing master at €1,000),{' '}
                <strong data-id="element-128" data-mp="3kgwoxn2bxjn6">country localization at €1,000</strong> per country per
                campaign, and{' '}
                <strong data-id="element-129" data-mp="2zwpytlsgna8">data insights & hosting at €99/month per country</strong>{' '}
                across the 3-month campaign run. Versus a traditional digital
                pharma
                campaign — banners, emails, WhatsApp GIFs plus strategy,
                creative, regulatory review and local adaptation — which costs{' '}
                <strong data-id="element-130" data-mp="2dtj13wbzs2dv">€15,000–€19,000 per campaign per country</strong>, the
                Snakker model lands a fully managed, localized deployment in the{' '}
                <strong data-id="element-131" data-mp="29tqsjjmllw8i">low thousands</strong>. ACV scales predictably as each
                account adds brands, campaigns and countries — against the
                industry benchmark of{' '}
                <strong data-id="element-132" data-mp="33a4becrzxw3s">
                  20–60 brands × 100–135 countries × 4–6 campaigns/year
                </strong>
                , a single account is worth millions in annual revenue.
              </p>

              <h3 className="text-base font-bold text-[#6C2BD9] uppercase tracking-wider mt-8 mb-2" data-id="element-133" data-mp="3c6d9no48mooc">
                Financial Projections — 2026–2029
              </h3>
              <p className="text-slate-700 leading-relaxed" data-id="element-134" data-mp="2kdw473ffbb5u">
                <strong data-id="element-135" data-mp="2oey31j5fhl7a">2026 expectation: DKK 3.9M</strong> in revenue (gross
                margin DKK 2.93M, net result −DKK 2.95M) — the investment year.
                From there the budget scales{' '}
                <strong data-id="element-136" data-mp="3oz0xqb2e6cl">DKK 12.82M (2027) → 22.75M (2028) → 36M (2029)</strong>,
                a 3.3× step in 2027 alone. The business{' '}
                <strong data-id="element-137" data-mp="8lz5txw6h324">breaks even already in 2027</strong> with a net result
                of <strong data-id="element-138" data-mp="iqfmuule8q7i">+DKK 0.35M</strong>, then compounds to{' '}
                <strong data-id="element-139" data-mp="dsnvc1onv63u">+DKK 5.45M in 2028</strong> and{' '}
                <strong data-id="element-140" data-mp="dt5vh9eg24h1">+DKK 18.6M in 2029</strong> on a gross margin rising
                from 75% to 85%.
              </p>

              <h3 className="text-base font-bold text-[#6C2BD9] uppercase tracking-wider mt-8 mb-2" data-id="element-141" data-mp="502bsjd7gvdy">
                In One Line
              </h3>
              <p className="text-lg font-semibold text-slate-900 leading-snug mt-2 pb-4" data-id="element-142" data-mp="1sfg5fqvoqr">
                Snakker is Campaign Service Management for legacy pharma —
                moving our 14 named pharma accounts from pilots into the
                campaign-calendar model already running inside GSK, Solventum,
                Leo Pharma and Roche.
              </p>
            </article>
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}